"use client";

import * as React from "react";
import * as LabelPrimitive from "@radix-ui/react-label";
import { cn } from "@/lib/utils";

const Label = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => (
  <LabelPrimitive.Root
    ref={ref}
    className={cn(
      "text-xs font-semibold leading-none text-text-primary peer-disabled:cursor-not-allowed peer-disabled:opacity-70 select-none",
      className,
    )}
    {...props}
  />
));
Label.displayName = "Label";

const Input = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>(({ className, type = "text", ...props }, ref) => {
  return (
    <input
      type={type}
      className={cn(
        "flex h-9 w-full rounded-lg border border-border bg-card px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:border-primary-500 disabled:cursor-not-allowed disabled:opacity-50 text-foreground",
        className,
      )}
      ref={ref}
      {...props}
    />
  );
});
Input.displayName = "Input";

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  placeholder?: string;
}

const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      className,
      children,
      value,
      defaultValue,
      onChange,
      placeholder,
      ...props
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [localValue, setLocalValue] = React.useState(
      defaultValue !== undefined ? String(defaultValue) : "",
    );
    const containerRef = React.useRef<HTMLDivElement>(null);

    // Sync local value with controlled value prop
    React.useEffect(() => {
      if (value !== undefined) {
        setLocalValue(String(value));
      }
    }, [value]);

    // Extract options from children
    const options = React.useMemo(() => {
      const list: { value: string; label: string }[] = [];
      React.Children.forEach(children, (child) => {
        if (React.isValidElement(child)) {
          const element = child as React.ReactElement<any>;
          const getLabel = (node: any): string => {
            if (Array.isArray(node)) {
              return node
                .map((n) => (n !== null && n !== undefined ? String(n) : ""))
                .join("");
            }
            return node !== null && node !== undefined ? String(node) : "";
          };
          if (element.type === "option") {
            list.push({
              value: String(element.props.value || ""),
              label: getLabel(element.props.children),
            });
          } else if (element.type === React.Fragment) {
            React.Children.forEach(element.props.children, (nested) => {
              if (React.isValidElement(nested)) {
                const nestedElement = nested as React.ReactElement<any>;
                if (nestedElement.type === "option") {
                  list.push({
                    value: String(nestedElement.props.value || ""),
                    label: getLabel(nestedElement.props.children),
                  });
                }
              }
            });
          }
        }
      });
      return list;
    }, [children]);

    const selectedValue = value !== undefined ? String(value) : localValue;
    const selectedOption =
      options.find((opt) => opt.value === selectedValue) || options[0];

    // Click outside listener
    React.useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    const handleSelect = (val: string) => {
      setIsOpen(false);
      setLocalValue(val);
      if (onChange) {
        const event = {
          target: {
            name: props.name,
            value: val,
          },
          currentTarget: {
            name: props.name,
            value: val,
          },
        } as unknown as React.ChangeEvent<HTMLSelectElement>;
        onChange(event);
      }
    };

    return (
      <div ref={containerRef} className="relative w-full">
        {/* Native select hidden but accessible */}
        <select
          ref={ref}
          value={selectedValue}
          onChange={onChange}
          className="sr-only"
          {...props}
        >
          {children}
        </select>

        {/* Premium trigger button */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            "flex h-9 w-full items-center justify-between rounded-lg border border-border bg-card px-3 py-1.5 text-sm shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:cursor-not-allowed disabled:opacity-50 text-foreground cursor-pointer select-none text-left",
            className,
          )}
          disabled={props.disabled}
        >
          <span className="truncate">
            {selectedOption
              ? selectedOption.label
              : placeholder || "Seleziona..."}
          </span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={cn(
              "h-4 w-4 text-text-secondary transition-transform duration-200 shrink-0 ml-2",
              isOpen && "rotate-180",
            )}
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>

        {/* Premium dropdown panel */}
        {isOpen && (
          <div className="absolute left-0 mt-1 w-max min-w-full max-w-[320px] rounded-xl border border-border bg-card p-1 shadow-lg z-50 animate-in fade-in slide-in-from-top-1 duration-200">
            <div className="max-h-60 overflow-y-auto scrollbar-thin space-y-0.5">
              {options.map((opt) => {
                const isSelected = opt.value === selectedValue;
                return (
                  <div
                    key={opt.value}
                    onClick={() => handleSelect(opt.value)}
                    className={cn(
                      "flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm cursor-pointer select-none transition-colors",
                      isSelected
                        ? "bg-primary-500/10 text-primary-600 font-semibold"
                        : "text-text-primary hover:bg-card-muted",
                    )}
                  >
                    <span className="whitespace-nowrap pr-6">{opt.label}</span>
                    {isSelected && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-4 w-4 text-primary-500 ml-2 shrink-0"
                      >
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  },
);
Select.displayName = "Select";

function getDaysInMonth(year: number, month: number) {
  const date = new Date(year, month, 1);
  const days: { date: Date; isCurrentMonth: boolean }[] = [];

  let firstDayIndex = date.getDay() - 1;
  if (firstDayIndex < 0) firstDayIndex = 6;

  const prevMonthDate = new Date(year, month, 0);
  const prevMonthDaysCount = prevMonthDate.getDate();
  for (let i = firstDayIndex - 1; i >= 0; i--) {
    days.push({
      date: new Date(year, month - 1, prevMonthDaysCount - i),
      isCurrentMonth: false,
    });
  }

  const daysCount = new Date(year, month + 1, 0).getDate();
  for (let i = 1; i <= daysCount; i++) {
    days.push({
      date: new Date(year, month, i),
      isCurrentMonth: true,
    });
  }

  const nextMonthFillerCount = 42 - days.length;
  for (let i = 1; i <= nextMonthFillerCount; i++) {
    days.push({
      date: new Date(year, month + 1, i),
      isCurrentMonth: false,
    });
  }

  return days;
}

interface DatePickerProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange"
> {
  value?: string;
  onChange?: (val: string) => void;
}

const DatePicker = React.forwardRef<HTMLInputElement, DatePickerProps>(
  (
    { className, value, defaultValue, onChange, placeholder, ...props },
    ref,
  ) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const containerRef = React.useRef<HTMLDivElement>(null);

    const initialDate = React.useMemo(() => {
      const val = value || defaultValue || "";
      if (val && typeof val === "string") {
        const parts = val.split("-");
        if (parts.length === 3) {
          const y = parseInt(parts[0], 10);
          const m = parseInt(parts[1], 10) - 1;
          const d = parseInt(parts[2], 10);
          return new Date(y, m, d);
        }
      }
      return new Date();
    }, [value, defaultValue]);

    const [currentYear, setCurrentYear] = React.useState(
      initialDate.getFullYear(),
    );
    const [currentMonth, setCurrentMonth] = React.useState(
      initialDate.getMonth(),
    );

    const selectedDateString =
      value !== undefined
        ? String(value)
        : defaultValue
          ? String(defaultValue)
          : "";

    React.useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    const days = React.useMemo(() => {
      return getDaysInMonth(currentYear, currentMonth);
    }, [currentYear, currentMonth]);

    const handleSelectDay = (date: Date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      const dateStr = `${y}-${m}-${d}`;
      setIsOpen(false);
      if (onChange) {
        onChange(dateStr);
      }
    };

    const handlePrevMonth = () => {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    };

    const handleNextMonth = () => {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    };

    const handleClear = () => {
      setIsOpen(false);
      if (onChange) {
        onChange("");
      }
    };

    const handleToday = () => {
      const today = new Date();
      setCurrentYear(today.getFullYear());
      setCurrentMonth(today.getMonth());
      handleSelectDay(today);
    };

    const formattedDisplayValue = React.useMemo(() => {
      if (!selectedDateString) return "";
      const parts = selectedDateString.split("-");
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      return selectedDateString;
    }, [selectedDateString]);

    const monthsNames = [
      "Gennaio",
      "Febbraio",
      "Marzo",
      "Aprile",
      "Maggio",
      "Giugno",
      "Luglio",
      "Agosto",
      "Settembre",
      "Ottobre",
      "Novembre",
      "Dicembre",
    ];

    return (
      <div ref={containerRef} className="relative w-full">
        <input
          ref={ref}
          type="hidden"
          value={selectedDateString}
          required={props.required}
          name={props.name}
        />

        <div className="relative">
          <input
            type="text"
            readOnly
            onClick={() => !props.disabled && setIsOpen(!isOpen)}
            placeholder={placeholder || "dd/mm/yyyy"}
            value={formattedDisplayValue}
            className={cn(
              "flex h-9 w-full rounded-lg border border-border bg-card pl-3 pr-10 py-1.5 text-sm shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:cursor-not-allowed disabled:opacity-50 text-foreground cursor-pointer select-none text-left",
              className,
            )}
            disabled={props.disabled}
          />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            onClick={() => !props.disabled && setIsOpen(!isOpen)}
            className="absolute right-3 top-2.5 h-4 w-4 text-text-secondary cursor-pointer"
          >
            <path d="M8 2v4" />
            <path d="M16 2v4" />
            <rect width="18" height="18" x="3" y="4" rx="2" />
            <path d="M3 10h18" />
          </svg>
        </div>

        {isOpen && (
          <div className="absolute left-0 mt-1 w-[280px] rounded-xl border border-border bg-card p-3 shadow-lg z-50 animate-in fade-in slide-in-from-top-1 duration-200">
            <div className="flex items-center justify-between pb-2 border-b border-border/40 mb-2">
              <button
                type="button"
                onClick={handlePrevMonth}
                className="p-1.5 hover:bg-card-muted rounded-md transition-colors text-text-secondary hover:text-text-primary"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m15 18-6-6 6-6" />
                </svg>
              </button>

              <span className="text-xs font-bold text-text-primary">
                {monthsNames[currentMonth]} {currentYear}
              </span>

              <button
                type="button"
                onClick={handleNextMonth}
                className="p-1.5 hover:bg-card-muted rounded-md transition-colors text-text-secondary hover:text-text-primary"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold text-text-muted mb-1">
              <span>Lu</span>
              <span>Ma</span>
              <span>Me</span>
              <span>Gi</span>
              <span>Ve</span>
              <span>Sa</span>
              <span>Do</span>
            </div>

            <div className="grid grid-cols-7 gap-0.5">
              {days.map((day, idx) => {
                const dayStr = `${day.date.getFullYear()}-${String(day.date.getMonth() + 1).padStart(2, "0")}-${String(day.date.getDate()).padStart(2, "0")}`;
                const isSelected = dayStr === selectedDateString;

                const todayObj = new Date();
                const isToday =
                  day.date.getDate() === todayObj.getDate() &&
                  day.date.getMonth() === todayObj.getMonth() &&
                  day.date.getFullYear() === todayObj.getFullYear();

                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleSelectDay(day.date)}
                    className={cn(
                      "h-8 w-8 text-xs rounded-md flex items-center justify-center transition-all cursor-pointer font-medium",
                      !day.isCurrentMonth && "text-text-muted/40",
                      day.isCurrentMonth && "text-text-primary",
                      isToday &&
                        !isSelected &&
                        "border border-primary-500/50 text-primary-600 font-bold",
                      isSelected
                        ? "bg-primary-500 text-text-on-primary font-bold shadow-xs hover:bg-primary-600"
                        : "hover:bg-card-muted",
                    )}
                  >
                    {day.date.getDate()}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-border/40 mt-2 text-xs font-semibold">
              <button
                type="button"
                onClick={handleClear}
                className="text-text-secondary hover:text-error transition-colors px-2 py-1 rounded hover:bg-card-muted cursor-pointer"
              >
                Cancella
              </button>
              <button
                type="button"
                onClick={handleToday}
                className="text-primary-600 hover:text-primary-700 transition-colors px-2 py-1 rounded hover:bg-primary-500/5 cursor-pointer"
              >
                Oggi
              </button>
            </div>
          </div>
        )}
      </div>
    );
  },
);
DatePicker.displayName = "DatePicker";

const ErrorMessage = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, children, ...props }, ref) => {
  if (!children) return null;
  return (
    <p
      ref={ref}
      className={cn("text-xs font-medium text-error mt-1", className)}
      {...props}
    >
      {children}
    </p>
  );
});
ErrorMessage.displayName = "ErrorMessage";

export { Label, Input, Select, DatePicker, ErrorMessage };
